import styled, { css, keyframes } from "styled-components";
import { colorMixin, hexToRGBA, spacingMixin, typographyMixin } from "../../helpers";

const moveToLeft = keyframes`
    0% {
        transform: translateX(200px);
        opacity: 0;
    }
    100% {
        transform: translateX(0px);
        opacity: 1;
    }
`;

const opacityInAnimation = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const opacityOutAnimation = keyframes`
    0% {
        opacity: 1;
    }
    100% {
        opacity: 0;
    }
`;

export const PopupOverlay = styled.div<{
    open: boolean;
    duration?: number;
    backgroundColor?: string;
    $disableAnimation: boolean;
}>`
    ${typographyMixin("paragraph-regular-regular")};

    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 99;
    overflow: auto;

    ${({ theme, backgroundColor }) => `
        background-color: ${backgroundColor ?? hexToRGBA(theme.v2.colors["primary-1200"], 0.2)};
    `}

    ${({ $disableAnimation, open, duration }) =>
        !$disableAnimation &&
        css`
            animation-name: ${open ? opacityInAnimation : opacityOutAnimation};
            animation-duration: ${duration}ms;
            animation-timing-function: ease-in-out;
            animation-fill-mode: forwards;
        `}
`;

export const ContextContainer = styled.div`
    position: relative;

    .bottomPositioning {
        min-width: 245px;
        top: 36px;
        right: 0;
        padding: 0;
        max-height: calc(100vh - 20em);
        overflow: auto;
    }
`;

export const FilterWrapper = styled.div<{ $disableAnimation: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: fixed;
    overflow: scroll;
    height: 100%;
    top: 0;
    right: 0;
    z-index: 3;
    padding: 32px 24px;
    gap: ${spacingMixin(3)};
    background: ${colorMixin("background-primary-base")};
    box-shadow: 0 12px 24px rgba(83, 86, 88, 0.1);

    ${({ $disableAnimation }) =>
        !$disableAnimation &&
        css`
            animation-name: ${moveToLeft};
            animation-duration: 200ms;
            animation-timing-function: ease-in-out;
            animation-fill-mode: forwards;
        `}
`;

export const FilterColumn = styled.div<{ $divider?: boolean }>`
    display: flex;
    flex-direction: column;
    ${({ $divider = false }) =>
        !$divider &&
        css`
            min-width: 280px;
        `}
    gap: ${spacingMixin(3)};
`;

export const FilterRow = styled.div<{ $divider?: boolean }>`
    display: flex;
    flex-direction: row;
    width: min-content;
    min-width: 100%;
    gap: ${spacingMixin(3)};
`;

export const FilterOptionButton = styled.button`
    cursor: pointer;

    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${spacingMixin(1)};

    border: none;
    background-color: ${colorMixin("background-primary-base")};
`;

export const FilterOptionNestedFilters = styled.div`
    padding-left: 24px;

    > * {
        min-width: auto;
    }
`;

export const Divider = styled.div<{ $orientation: "row" | "column" }>`
    flex: 1;

    display: flex;
    flex-direction: ${({ $orientation }) => $orientation};
    align-self: center;
    gap: ${spacingMixin(1)};

    ${({ $orientation }) =>
        $orientation === "column"
            ? css`
                  width: min-content;
                  white-space: pre-wrap;
                  text-align: center;
              `
            : css`
                  height: 20px;
                  white-space: nowrap;
              `};

    ::after {
        --divider-border-styling: 1px solid ${colorMixin("surface-100")};

        content: "";
        align-self: center;

        ${({ $orientation }) =>
            $orientation === "column"
                ? css`
                      height: 100%;
                      border-left: var(--divider-border-styling);
                  `
                : css`
                      width: 100%;
                      border-top: var(--divider-border-styling);
                  `};
    }
`;
